import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const WhatsAppSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [isConfigured, setIsConfigured] = useState(null);
  const [config, setConfig] = useState({
    phoneNumberId: '',
    accountId: '',
    accessToken: '',
    isActive: false,
    hasToken: false
  });
  const [formData, setFormData] = useState({
    phoneNumberId: '',
    accountId: '',
    accessToken: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showToken, setShowToken] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  // Helper to get user data from context or localStorage
  const getUserData = () => {
    if (user?.tenantId && user?._id) return user;
    const storedUserData = localStorage.getItem('userData');
    return storedUserData ? JSON.parse(storedUserData) : null;
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const userData = getUserData();
      if (!userData?.tenantId || !userData?._id) {
        // Just stop loading if no user data - don't throw error
        setLoading(false);
        setIsConfigured(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/whatsapp-config/config`, {
        headers: {
          tenantid: userData.tenantId,
          userid: userData._id || userData.userid
        }
      });

      if (response.data.status === 'success') {
        setConfig(response.data.data);
        setIsConfigured(true);
        setFormData({
          phoneNumberId: response.data.data.phoneNumberId,
          accountId: response.data.data.accountId,
          accessToken: ''
        });
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      // Config doesn't exist yet (404)
      if (error.response?.status === 404) {
        setIsConfigured(false);
      } else {
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Error al cargar la configuración'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.phoneNumberId || !formData.accountId || !formData.accessToken) {
      setMessage({
        type: 'error',
        text: 'All fields are required'
      });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const response = await axios.post(
        `${API_URL}/api/whatsapp-config/config`,
        formData,
        {
          headers: {
            tenantid: getUserData().tenantId,
            userid: getUserData()._id || getUserData().userid
          }
        }
      );

      if (response.data.status === 'success') {
        setMessage({
          type: 'success',
          text: 'WhatsApp configuration saved successfully!'
        });
        fetchConfig();
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error saving configuration'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setMessage({ type: '', text: '' });

      const response = await axios.post(
        `${API_URL}/api/whatsapp-config/config/test`,
        {},
        {
          headers: {
            tenantid: getUserData().tenantId,
            userid: getUserData()._id || getUserData().userid
          }
        }
      );

      if (response.data.status === 'success') {
        setMessage({
          type: 'success',
          text: 'Connection test successful! WhatsApp is working correctly.'
        });
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate WhatsApp integration?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/api/whatsapp-config/config/deactivate`,
        {},
        {
          headers: {
            tenantid: getUserData().tenantId,
            userid: getUserData()._id || getUserData().userid
          }
        }
      );

      if (response.data.status === 'success') {
        setMessage({
          type: 'success',
          text: 'WhatsApp configuration deactivated'
        });
        fetchConfig();
      }
    } catch (error) {
      console.error('Error deactivating config:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error deactivating configuration'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete WhatsApp configuration? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(
        `${API_URL}/api/whatsapp-config/config`,
        {
          headers: {
            tenantid: getUserData().tenantId,
            userid: getUserData()._id || getUserData().userid
          }
        }
      );

      if (response.data.status === 'success') {
        setMessage({
          type: 'success',
          text: 'WhatsApp configuration deleted'
        });
        setFormData({
          phoneNumberId: '',
          accountId: '',
          accessToken: ''
        });
        fetchConfig();
      }
    } catch (error) {
      console.error('Error deleting config:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error deleting configuration'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">WhatsApp Configuration</h2>
          <p className="text-gray-600">
            Configure your WhatsApp Business API credentials to enable messaging functionality.
          </p>
        </div>

        {/* Not Configured Info Box */}
        {isConfigured === false && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-blue-800 font-medium">WhatsApp no fue configurado todavía</p>
                <p className="text-blue-700 text-sm mt-1">Ingresa tus credenciales de WhatsApp Business API abajo para comenzar.</p>
              </div>
            </div>
          </div>
        )}

        {/* Status Badge */}
        {config.isActive && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
              <span className="text-green-800 font-medium">WhatsApp is Active</span>
            </div>
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          </div>
        )}

        {/* Alert Messages */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800'
              : message.type === 'info'
              ? 'bg-blue-50 border border-blue-200 text-blue-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Configuration Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Phone Number ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number ID *
            </label>
            <input
              type="text"
              name="phoneNumberId"
              value={formData.phoneNumberId}
              onChange={handleInputChange}
              placeholder="e.g., 102204909606419"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            <p className="mt-1 text-sm text-gray-500">
              Found in your WhatsApp Business API settings
            </p>
          </div>

          {/* Account ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              WhatsApp Business Account ID *
            </label>
            <input
              type="text"
              name="accountId"
              value={formData.accountId}
              onChange={handleInputChange}
              placeholder="e.g., 102204909606419"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            <p className="mt-1 text-sm text-gray-500">
              Your WhatsApp Business Account ID from Meta
            </p>
          </div>

          {/* Access Token */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Access Token *
            </label>
            <div className="relative">
              <input
                type={showToken ? 'text' : 'password'}
                name="accessToken"
                value={formData.accessToken}
                onChange={handleInputChange}
                placeholder={config.hasToken ? '••••••••••••••••' : 'Enter your access token'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                required={!config.hasToken}
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showToken ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Generate a permanent token from Meta Business Manager
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {loading ? 'Saving...' : 'Save Configuration'}
            </button>

            {config.isActive && (
              <>
                <button
                  type="button"
                  onClick={handleDeactivate}
                  disabled={loading}
                  className="px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 font-medium"
                >
                  Deactivate
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </form>

        {/* Help Section */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">How to get your credentials:</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
            <li>Go to <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">Meta Business Manager</a></li>
            <li>Navigate to WhatsApp API settings</li>
            <li>Copy your Phone Number ID and Account ID</li>
            <li>Generate a permanent access token</li>
            <li>Paste all credentials above and save</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSettings;
